import React, { useEffect, useState } from "react";
import { Input, FormGroup, Label, Button, Spinner } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getStateList } from "../../redux/actions/indexActions";

const defaultSection = () => ({
  heading: "",
  points: [{ text: "" }],
});

const defaultContent = () => [
  {
    heading: "Health & Safety",
    points: [{ text: "You must wear a mask in public places." }],
  },
  {
    heading: "Inner Line Permission (ILP)",
    points: [
      { text: "Apply for Inner Line Permit" },
      { text: "Click here to apply for Inner Line Permit" },
    ],
  },
  {
    heading: "Responsible Travel",
    points: [
      { text: "Be a responsible traveler." },
      { text: "Do not litter or harm the environment." },
    ],
  },
];

const FOR_OPTIONS = [
  { value: "state", label: "State" },
  { value: "city", label: "City" },
  { value: "town", label: "Town" },
  { value: "place", label: "Place" },
];

const AddInnerLinePermission = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    AuthReducer: { auth },
    indexReducer: { stateLists },
  } = useSelector((state) => state);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [cities, setCities] = useState([]);
  const [towns, setTowns] = useState([]);
  const [places, setPlaces] = useState([]);
  const [skipCascadeReset, setSkipCascadeReset] = useState(isEdit);

  const [formData, setFormData] = useState({
    for: "",
    state_id: "",
    city_id: "",
    town_id: "",
    place_id: "",
    is_apply_permit: 0,
    redirect_url: "",
    status: 1,
    content: defaultContent(),
  });

  useEffect(() => {
    if (auth?.token) dispatch(getStateList(auth.token));
  }, [auth?.token, dispatch]);

  useEffect(() => {
    if (!isEdit || !auth?.token) return;

    setFetching(true);
    axios
      .get(`/api/admin/v1/inner-line-permission/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      })
      .then((res) => {
        const data = res.data?.data;
        if (!data) {
          toast.error("Record not found");
          navigate("/inner-line-permission");
          return;
        }
        setSkipCascadeReset(true);
        setFormData({
          for: data.for || "",
          state_id: data.state_id ? String(data.state_id) : "",
          city_id: data.city_id ? String(data.city_id) : "",
          town_id: data.town_id ? String(data.town_id) : "",
          place_id: data.place_id ? String(data.place_id) : "",
          is_apply_permit: Number(data.is_apply_permit) ? 1 : 0,
          redirect_url: data.redirect_url || "",
          status: Number(data.status) ? 1 : 0,
          content:
            Array.isArray(data.content) && data.content.length
              ? data.content.map((section) => ({
                  heading: section?.heading || "",
                  points:
                    Array.isArray(section?.points) && section.points.length
                      ? section.points.map((point) => ({
                          text: point?.text || "",
                        }))
                      : [{ text: "" }],
                }))
              : defaultContent(),
        });
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to load record");
        navigate("/inner-line-permission");
      })
      .finally(() => setFetching(false));
  }, [id, isEdit, auth?.token, navigate]);

  useEffect(() => {
    if (!formData.state_id) {
      setCities([]);
      return;
    }
    axios(`/api/admin/v1/get-city/${formData.state_id}`)
      .then(({ data }) => setCities(data?.data || []))
      .catch(() => setCities([]));
  }, [formData.state_id]);

  useEffect(() => {
    if (!formData.state_id || !formData.city_id) {
      setTowns([]);
      return;
    }
    axios(`/api/admin/v1/get-town/${formData.state_id}/${formData.city_id}`)
      .then(({ data }) => setTowns(data?.data || []))
      .catch(() => setTowns([]));
  }, [formData.state_id, formData.city_id]);

  useEffect(() => {
    if (!auth?.token || !formData.state_id) {
      setPlaces([]);
      return;
    }
    const params = { state_id: formData.state_id };
    if (formData.city_id) params.city_id = formData.city_id;
    if (formData.town_id) params.town_id = formData.town_id;

    axios
      .get("/api/admin/v1/inner-line-permission-places", {
        headers: { Authorization: `Bearer ${auth.token}` },
        params,
      })
      .then((res) => setPlaces(res.data?.data || []))
      .catch(() => setPlaces([]));
  }, [auth?.token, formData.state_id, formData.city_id, formData.town_id]);

  const inputHandler = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" || name === "is_apply_permit" || name === "status") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked || value === "1" || value === 1 ? 1 : 0,
      }));
      return;
    }

    if (name === "for") {
      setFormData((prev) => ({
        ...prev,
        for: value,
      }));
      return;
    }

    if (name === "state_id") {
      setFormData((prev) => ({
        ...prev,
        state_id: value,
        city_id: "",
        town_id: "",
        place_id: "",
      }));
      setSkipCascadeReset(false);
      return;
    }

    if (name === "city_id") {
      setFormData((prev) => ({
        ...prev,
        city_id: value,
        town_id: "",
        place_id: "",
      }));
      setSkipCascadeReset(false);
      return;
    }

    if (name === "town_id") {
      setFormData((prev) => ({
        ...prev,
        town_id: value,
        place_id: skipCascadeReset ? prev.place_id : "",
      }));
      setSkipCascadeReset(false);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateSectionHeading = (sectionIndex, value) => {
    setFormData((prev) => {
      const content = [...prev.content];
      content[sectionIndex] = { ...content[sectionIndex], heading: value };
      return { ...prev, content };
    });
  };

  const updatePointText = (sectionIndex, pointIndex, value) => {
    setFormData((prev) => {
      const content = [...prev.content];
      const points = [...content[sectionIndex].points];
      points[pointIndex] = { text: value };
      content[sectionIndex] = { ...content[sectionIndex], points };
      return { ...prev, content };
    });
  };

  const addSection = () => {
    setFormData((prev) => ({
      ...prev,
      content: [...prev.content, defaultSection()],
    }));
  };

  const removeSection = (sectionIndex) => {
    setFormData((prev) => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== sectionIndex),
    }));
  };

  const addPoint = (sectionIndex) => {
    setFormData((prev) => {
      const content = [...prev.content];
      content[sectionIndex] = {
        ...content[sectionIndex],
        points: [...content[sectionIndex].points, { text: "" }],
      };
      return { ...prev, content };
    });
  };

  const removePoint = (sectionIndex, pointIndex) => {
    setFormData((prev) => {
      const content = [...prev.content];
      const points = content[sectionIndex].points.filter((_, i) => i !== pointIndex);
      content[sectionIndex] = {
        ...content[sectionIndex],
        points: points.length ? points : [{ text: "" }],
      };
      return { ...prev, content };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const payload = {
      for: formData.for || null,
      state_id: formData.state_id || null,
      city_id: formData.city_id || null,
      town_id: formData.town_id || null,
      place_id: formData.place_id || null,
      is_apply_permit: Number(formData.is_apply_permit) ? 1 : 0,
      redirect_url: formData.redirect_url,
      status: Number(formData.status) ? 1 : 0,
      content: formData.content,
    };

    const request = isEdit
      ? axios.put(`/api/admin/v1/inner-line-permission/${id}`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth?.token}`,
          },
        })
      : axios.post("/api/admin/v1/add-inner-line-permission", payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth?.token}`,
          },
        });

    request
      .then((res) => {
        toast.success(
          res.data?.message ||
            (isEdit ? "Updated successfully" : "Added successfully")
        );
        navigate("/inner-line-permission");
      })
      .catch((err) => {
        const data = err.response?.data;
        if (typeof data === "object" && data !== null) {
          setErrors(data);
          toast.error(data.message || "Something went wrong");
        } else {
          toast.error("Something went wrong");
        }
      })
      .finally(() => setLoading(false));
  };

  if (fetching) {
    return (
      <div className="text-center py-5">
        <Spinner color="light" />
      </div>
    );
  }

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <h4 className="text-white">
            {isEdit ? "Edit Inner Line Permission" : "Add Inner Line Permission"}
          </h4>
        </div>
      </div>
      <div className="bg-white px-3 py-3 rounded-2 my-4">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <FormGroup>
                <Label>
                  For <span className="text-danger fs-4">*</span>
                </Label>
                <Input
                  type="select"
                  name="for"
                  value={formData.for}
                  onChange={inputHandler}
                  invalid={Boolean(errors?.for)}
                >
                  <option value="">Select For</option>
                  {FOR_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Input>
                <span className="d-block text-danger">{errors?.for}</span>
              </FormGroup>
            </div>

            <div className="col-md-6 mb-3">
              <FormGroup>
                <Label>State</Label>
                <Input
                  type="select"
                  name="state_id"
                  value={formData.state_id}
                  onChange={inputHandler}
                >
                  <option value="">Select State</option>
                  {stateLists?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.state_name}
                    </option>
                  ))}
                </Input>
                <span className="d-block text-danger">{errors?.state_id}</span>
              </FormGroup>
            </div>

            <div className="col-md-6 mb-3">
              <FormGroup>
                <Label>City</Label>
                <Input
                  type="select"
                  name="city_id"
                  value={formData.city_id}
                  onChange={inputHandler}
                >
                  <option value="">Select City</option>
                  {cities?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.city_name}
                    </option>
                  ))}
                </Input>
                <span className="d-block text-danger">{errors?.city_id}</span>
              </FormGroup>
            </div>

            <div className="col-md-6 mb-3">
              <FormGroup>
                <Label>Town</Label>
                <Input
                  type="select"
                  name="town_id"
                  value={formData.town_id}
                  onChange={inputHandler}
                >
                  <option value="">Select Town</option>
                  {towns?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.town_name}
                    </option>
                  ))}
                </Input>
                <span className="d-block text-danger">{errors?.town_id}</span>
              </FormGroup>
            </div>

            <div className="col-md-6 mb-3">
              <FormGroup>
                <Label>Place</Label>
                <Input
                  type="select"
                  name="place_id"
                  value={formData.place_id}
                  onChange={inputHandler}
                >
                  <option value="">Select Place</option>
                  {places?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.place_name}
                    </option>
                  ))}
                </Input>
                <span className="d-block text-danger">{errors?.place_id}</span>
              </FormGroup>
            </div>

            <div className="col-md-6 mb-3">
              <FormGroup switch>
                <Input
                  type="switch"
                  name="is_apply_permit"
                  checked={Number(formData.is_apply_permit) === 1}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_apply_permit: e.target.checked ? 1 : 0,
                    }))
                  }
                />
                <Label check>Is Apply Permit</Label>
              </FormGroup>
            </div>

            <div className="col-md-6 mb-3">
              <FormGroup switch>
                <Input
                  type="switch"
                  name="status"
                  checked={Number(formData.status) === 1}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.checked ? 1 : 0,
                    }))
                  }
                />
                <Label check>Status</Label>
              </FormGroup>
            </div>

            {Number(formData.is_apply_permit) === 1 && (
              <div className="col-md-12 mb-3">
                <FormGroup>
                  <Label>Redirect URL</Label>
                  <Input
                    type="text"
                    name="redirect_url"
                    value={formData.redirect_url}
                    onChange={inputHandler}
                    placeholder="https://example.com/apply"
                  />
                  <span className="d-block text-danger">
                    {errors?.redirect_url}
                  </span>
                </FormGroup>
              </div>
            )}

            <div className="col-md-12 mb-2">
              <div className="d-flex align-items-center justify-content-between">
                <Label className="mb-0 fw-bold">Content Sections</Label>
                <Button type="button" color="primary" size="sm" onClick={addSection}>
                  + Add Section
                </Button>
              </div>
              <span className="d-block text-danger">{errors?.content}</span>
            </div>

            {formData.content.map((section, sectionIndex) => (
              <div key={sectionIndex} className="col-md-12 mb-3">
                <div className="border rounded p-3">
                  <div className="d-flex align-items-end gap-2 mb-3">
                    <FormGroup className="flex-grow-1 mb-0">
                      <Label>Heading</Label>
                      <Input
                        type="text"
                        value={section.heading}
                        onChange={(e) =>
                          updateSectionHeading(sectionIndex, e.target.value)
                        }
                        placeholder="e.g. Health & Safety"
                      />
                    </FormGroup>
                    <Button
                      type="button"
                      color="danger"
                      size="sm"
                      className="mb-1"
                      onClick={() => removeSection(sectionIndex)}
                      disabled={formData.content.length <= 1}
                    >
                      Remove Section
                    </Button>
                  </div>

                  {section.points.map((point, pointIndex) => (
                    <div
                      key={pointIndex}
                      className="d-flex align-items-end gap-2 mb-2"
                    >
                      <FormGroup className="flex-grow-1 mb-0">
                        <Label>Point {pointIndex + 1}</Label>
                        <Input
                          type="text"
                          value={point.text}
                          onChange={(e) =>
                            updatePointText(
                              sectionIndex,
                              pointIndex,
                              e.target.value
                            )
                          }
                          placeholder="Enter point text"
                        />
                      </FormGroup>
                      <Button
                        type="button"
                        color="danger"
                        size="sm"
                        className="mb-1"
                        onClick={() => removePoint(sectionIndex, pointIndex)}
                      >
                        -
                      </Button>
                      {pointIndex === section.points.length - 1 && (
                        <Button
                          type="button"
                          color="primary"
                          size="sm"
                          className="mb-1"
                          onClick={() => addPoint(sectionIndex)}
                        >
                          +
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="col-md-12 mt-2">
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : isEdit ? "Update" : "Submit"}
              </Button>
              <Button
                className="ms-3"
                type="button"
                onClick={() => navigate("/inner-line-permission")}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInnerLinePermission;
